import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Briefcase,
  Save,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import configs from '../config';
import { fetchWithTenant } from '../utils/apiClient';
import { useTenant } from '../contexts/TenantContext';
import {
  getProfileFilterSuggestions,
  updateUpworkJobFilters,
  getProfileImportStatus,
  startOnboardingActivation,
} from '../services/tenantService';
import { trackAnalyticsEvent } from '../utils/analyticsHelper';
import {
  ONBOARDING_STEP_VIEWED,
  ONBOARDING_STEP_COMPLETED,
  ONBOARDING_STEP_FAILED,
} from '../constants/analyticsEvents';
import JobFilterSuggestionModal from './JobFilterSuggestionModal';

const API_BASE = `${configs.BACKEND_URL}/api`;

/**
 * Sub-step states: idle | loading | success | error
 */
const IDLE = 'idle';
const LOADING = 'loading';
const SUCCESS = 'success';
const ERROR = 'error';

/**
 * Guided Upwork connect step for the onboarding flow.
 * Sequential: Save credentials → Initiate OAuth → Connected
 *
 * Props:
 *  - onComplete: () => void  — called when OAuth status becomes connected
 *  - onRefresh: () => void   — trigger parent step refresh
 */
export default function OnboardingUpworkStep({ onComplete, onRefresh }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();

  // Sub-step states
  const [credentialState, setCredentialState] = useState(IDLE);
  const [oauthState, setOauthState] = useState(IDLE);
  const [credentialError, setCredentialError] = useState(null);
  const [oauthError, setOauthError] = useState(null);

  // Form
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // Status
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionSaving, setSuggestionSaving] = useState(false);
  const [suggestionError, setSuggestionError] = useState(null);
  const [suggestionSaveError, setSuggestionSaveError] = useState(null);
  const [suggestionData, setSuggestionData] = useState(null);
  const [profileImportStatus, setProfileImportStatus] = useState(null);
  const [activationWarning, setActivationWarning] = useState(null);

  // Track view
  useEffect(() => {
    trackAnalyticsEvent(ONBOARDING_STEP_VIEWED, { step_id: 'connect_upwork', context: 'onboarding' });
  }, []);

  // Check OAuth status on mount and after callback
  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const [statusRes, configRes] = await Promise.all([
        fetchWithTenant(`${API_BASE}/upwork/oauth/status`),
        fetchWithTenant(`${API_BASE}/tenant/config`),
      ]);
      const statusData = statusRes.ok ? await statusRes.json() : null;
      const configData = configRes.ok ? await configRes.json() : null;
      const importData = await getProfileImportStatus().catch(() => null);

      const connected = statusData?.data?.status === 'connected';
      const importReady = importData?.data?.ready === true;
      const hasClientId = Boolean(configData?.data?.upwork?.hasClientId);
      const hasClientSecret = Boolean(configData?.data?.upwork?.hasClientSecret);
      const hasCreds = hasClientId && hasClientSecret;

      setIsConnected(connected);
      setHasCredentials(hasCreds);
      setProfileImportStatus(importData?.data || null);

      if (connected || importReady) {
        setCredentialState(SUCCESS);
        setOauthState(connected ? SUCCESS : IDLE);
        trackAnalyticsEvent(ONBOARDING_STEP_COMPLETED, { step_id: 'connect_upwork', context: 'onboarding' });
        onComplete?.();
      } else if (hasCreds) {
        setCredentialState(SUCCESS);
      } else if (hasClientId && !hasClientSecret) {
        setCredentialState(ERROR);
        setCredentialError('Client Secret is missing. Please provide both Client ID and Client Secret.');
      }
    } catch {
      // Non-fatal; user can still proceed
    } finally {
      setStatusLoading(false);
    }
  }, [onComplete]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const loadProfileSuggestions = useCallback(async () => {
    setSuggestionLoading(true);
    setSuggestionError(null);
    setSuggestionSaveError(null);
    try {
      const response = await getProfileFilterSuggestions();
      setSuggestionData(response?.data || null);
    } catch (err) {
      setSuggestionError(err.message || 'Failed to load profile-based suggestions');
      setSuggestionData(null);
    } finally {
      setSuggestionLoading(false);
    }
  }, []);

  // Handle OAuth callback query params
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthErrorParam = searchParams.get('oauth_error');
    const activationWarningParam = searchParams.get('activation_warning');

    if (oauthSuccess === 'true') {
      setOauthState(SUCCESS);
      setActivationWarning(activationWarningParam ? decodeURIComponent(activationWarningParam) : null);
      startOnboardingActivation({ source: 'oauth', trigger: 'onboarding_connect' }).catch(() => {
        // Non-fatal: onboarding page will keep polling activation status
      });
      // Clean up URL
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('oauth_success');
      nextParams.delete('connected');
      nextParams.delete('suggest_filters');
      nextParams.delete('activation_warning');
      setSearchParams(nextParams, { replace: true });
      checkStatus();
    } else if (oauthErrorParam) {
      setOauthState(ERROR);
      setOauthError(oauthErrorParam);
      setActivationWarning(null);
      trackAnalyticsEvent(ONBOARDING_STEP_FAILED, {
        step_id: 'connect_upwork',
        sub_step: 'oauth',
        error: oauthErrorParam,
        context: 'onboarding',
      });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('oauth_error');
      nextParams.delete('activation_warning');
      setSearchParams(nextParams, { replace: true });
    }
  }, [checkStatus, loadProfileSuggestions, searchParams, setSearchParams]);

  const handleSuggestionSkip = () => {
    setShowSuggestionModal(false);
    setSuggestionSaveError(null);
    onRefresh?.();
  };

  const handleSuggestionSave = async () => {
    if (!suggestionData?.suggested) {
      handleSuggestionSkip();
      return;
    }

    setSuggestionSaving(true);
    setSuggestionSaveError(null);

    try {
      await updateUpworkJobFilters(tenant?._id, suggestionData.suggested);
      setShowSuggestionModal(false);
      onRefresh?.();
    } catch (err) {
      setSuggestionSaveError(err.message || 'Failed to save suggested filters');
    } finally {
      setSuggestionSaving(false);
    }
  };

  const suggestionModal = (
    <JobFilterSuggestionModal
      open={showSuggestionModal}
      loading={suggestionLoading}
      saving={suggestionSaving}
      error={suggestionError}
      saveError={suggestionSaveError}
      data={suggestionData}
      onSave={handleSuggestionSave}
      onSkip={handleSuggestionSkip}
    />
  );

  // Save credentials
  const handleSaveCredentials = async () => {
    const normalizedClientId = clientId.trim();
    const normalizedClientSecret = clientSecret.trim();
    if (!normalizedClientId || !normalizedClientSecret) {
      setCredentialState(ERROR);
      setCredentialError('Both Client ID and Client Secret are required.');
      return;
    }

    setCredentialState(LOADING);
    setCredentialError(null);

    try {
      const body = {
        clientId: normalizedClientId,
        clientSecret: normalizedClientSecret,
      };

      const res = await fetchWithTenant(`${API_BASE}/tenant/upwork/oauth-credentials`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || 'Failed to save credentials');
      }

      setCredentialState(SUCCESS);
      setHasCredentials(true);
    } catch (err) {
      setCredentialState(ERROR);
      setCredentialError(err.message);
      trackAnalyticsEvent(ONBOARDING_STEP_FAILED, {
        step_id: 'connect_upwork',
        sub_step: 'credentials',
        error: err.message,
        context: 'onboarding',
      });
    }
  };

  // Initiate OAuth
  const handleConnectOAuth = async () => {
    setOauthState(LOADING);
    setOauthError(null);

    try {
      const res = await fetchWithTenant(
        `${API_BASE}/upwork/oauth/initiate?returnTo=${encodeURIComponent('/onboarding')}`
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || 'Failed to initiate OAuth');
      }

      const { authorizationUrl } = await res.json();
      // Redirect to Upwork
      window.location.href = authorizationUrl;
    } catch (err) {
      setOauthState(ERROR);
      setOauthError(err.message);
      trackAnalyticsEvent(ONBOARDING_STEP_FAILED, {
        step_id: 'connect_upwork',
        sub_step: 'oauth_initiate',
        error: err.message,
        context: 'onboarding',
      });
    }
  };

  if (statusLoading) {
    return (
      <>
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Checking Upwork connection...</span>
        </div>
        {suggestionModal}
      </>
    );
  }

  // Already connected
  if (isConnected || profileImportStatus?.ready) {
    return (
      <>
        <div className={`rounded-lg border p-4 ${isConnected ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center gap-3">
            <CheckCircle2 className={`w-6 h-6 flex-shrink-0 ${isConnected ? 'text-green-600' : 'text-blue-600'}`} />
            <div>
              <p className={`font-medium ${isConnected ? 'text-green-800' : 'text-blue-800'}`}>
                {isConnected ? 'Upwork Connected' : 'Browser-Assist Mode Ready'}
              </p>
              <p className={`text-sm ${isConnected ? 'text-green-600' : 'text-blue-700'}`}>
                {isConnected
                  ? 'Your Upwork account is linked and ready to sync.'
                  : 'Your imported Upwork profile snapshot is ready for job analysis, proposal drafting, and extension-assisted applying.'}
              </p>
            </div>
          </div>
        </div>
        {activationWarning && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {activationWarning}
          </div>
        )}
        {suggestionModal}
      </>
    );
  }

  const credentialsDone = credentialState === SUCCESS || hasCredentials;

  return (
    <>
      {activationWarning && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {activationWarning}
        </div>
      )}
      <div className="space-y-4">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
          <h4 className="font-medium text-primary-900">Choose how Propel should access your Upwork data</h4>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-green-200 bg-white p-3">
              <p className="text-sm font-medium text-gray-900">Connect with Upwork OAuth</p>
              <p className="mt-1 text-xs text-gray-600">
                Full-sync mode: jobs, proposals, and messages sync automatically.
              </p>
            </div>
            <div
              className="rounded-lg border border-blue-200 bg-white p-3"
              data-onboarding="browser-assist-import-card"
            >
              <p className="text-sm font-medium text-gray-900">Import my Upwork profile pages</p>
              <p className="mt-1 text-xs text-gray-600">
                Browser-Assist mode: no API sync, but Propel can still analyze jobs, draft proposals, and help you apply through the extension.
              </p>
            </div>
          </div>
        </div>

        {/* Sub-step 1: Credentials */}
        <div
          className={`rounded-lg border p-4 transition-all ${
            credentialsDone
              ? 'border-green-200 bg-green-50'
              : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            {credentialsDone ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary-700">1</span>
              </div>
            )}
            <h4 className="font-medium text-gray-900">Configure OAuth Credentials</h4>
          </div>

          {!credentialsDone && (
            <>
              <p className="text-sm text-gray-600 mb-3 ml-8">
                Get your Client ID and Client Secret from{' '}
                <a
                  href="https://www.upwork.com/developer/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline inline-flex items-center gap-1"
                >
                  Upwork Developer Keys
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>

              <div className="ml-8 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Client ID *</label>
                  <input
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter Upwork Client ID"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Client Secret *</label>
                  <input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter Upwork Client Secret"
                  />
                </div>

                {credentialState === ERROR && credentialError && (
                  <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-md p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{credentialError}</span>
                  </div>
                )}

                <button
                  onClick={handleSaveCredentials}
                  disabled={!clientId.trim() || !clientSecret.trim() || credentialState === LOADING}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {credentialState === LOADING ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Credentials
                </button>
              </div>
            </>
          )}

          {credentialsDone && (
            <p className="text-sm text-green-600 ml-8">Credentials saved successfully.</p>
          )}
        </div>

        {/* Sub-step 2: OAuth Connect */}
        <div
          className={`rounded-lg border p-4 transition-all ${
            oauthState === SUCCESS
              ? 'border-green-200 bg-green-50'
              : credentialsDone
                ? 'border-gray-200 bg-white'
                : 'border-gray-100 bg-gray-50 opacity-60'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            {oauthState === SUCCESS ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary-700">2</span>
              </div>
            )}
            <h4 className="font-medium text-gray-900">Connect Upwork Account</h4>
          </div>

          {oauthState !== SUCCESS && (
            <div className="ml-8">
              <p className="text-sm text-gray-600 mb-3">
                Authorize Propel to access your Upwork account. You&apos;ll be redirected to Upwork and back.
              </p>

              {oauthState === ERROR && oauthError && (
                <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-md p-2 mb-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{oauthError}</span>
                </div>
              )}

              <button
                onClick={handleConnectOAuth}
                disabled={!credentialsDone || oauthState === LOADING}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {oauthState === LOADING ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : oauthState === ERROR ? (
                  <RefreshCw className="w-4 h-4" />
                ) : (
                  <Briefcase className="w-4 h-4" />
                )}
                {oauthState === ERROR ? 'Retry Connection' : 'Connect to Upwork'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {oauthState === SUCCESS && (
            <p className="text-sm text-green-600 ml-8">Upwork account connected successfully!</p>
          )}
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-blue-700">Alt</span>
            </div>
            <h4 className="font-medium text-gray-900">Browser-Assist Mode</h4>
          </div>

          <div className="ml-8 space-y-3">
            <p className="text-sm text-gray-700">
              Use this fallback if Upwork does not allow you to get OAuth credentials. Propel will use a reviewed profile snapshot captured from your Upwork pages inside the Chrome extension.
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Open your Upwork freelancer profile page.</li>
              <li>Open the Propel Chrome extension side panel.</li>
              <li>Review the parsed profile data and confirm the import.</li>
              <li>Repeat for portfolio or specialized profile pages if available.</li>
            </ul>

            {profileImportStatus && (
              <div className="rounded-md border border-blue-200 bg-white p-3 text-sm">
                <p className="font-medium text-blue-900">
                  {profileImportStatus.ready ? 'Import ready' : 'Waiting for imported profile pages'}
                </p>
                <p className="mt-1 text-blue-700">
                  Coverage: {profileImportStatus.coverage?.freelancerProfile ? 'Profile' : 'Profile missing'}
                  {' • '}
                  {profileImportStatus.coverage?.portfolio ? 'Portfolio added' : 'Portfolio missing'}
                  {' • '}
                  {profileImportStatus.coverage?.employmentEducation ? 'Experience added' : 'Experience optional'}
                </p>
                {profileImportStatus.missingRecommendedPages?.length > 0 && (
                  <p className="mt-1 text-blue-700">
                    Missing: {profileImportStatus.missingRecommendedPages.join(', ').replaceAll('_', ' ')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {suggestionModal}
    </>
  );
}
